<template>
  <div class="modal-overlay">
    <div class="modal">
      <div class="modal-header">
        <div class="header-left">
          <h2>{{ modalTitle }}</h2>
          <span v-if="prompt" :class="['badge', `badge-${prompt.status}`]">
            {{ $t(`bo.aiPrompts.status${capitalize(prompt.status || '')}`) }}
          </span>
          <span v-if="prompt" class="version-chip">v{{ prompt.version }}</span>
        </div>
        <button @click="$emit('close')" class="btn-close">&times;</button>
      </div>

      <!-- Tabs: Edit / Versions -->
      <div class="tabs">
        <button :class="['tab', { active: activeTab === 'edit' }]" @click="activeTab = 'edit'">
          {{ $t('bo.aiPrompts.tabEdit') }}
        </button>
        <button :class="['tab', { active: activeTab === 'versions' }]" @click="loadVersionsTab">
          {{ $t('bo.aiPrompts.tabVersions') }}
        </button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="loading">{{ $t('bo.common.loading') }}</div>

      <!-- Error loading -->
      <div v-else-if="loadError" class="error-full">{{ loadError }}</div>

      <template v-else-if="prompt">
        <!-- Tab: Edit -->
        <div v-if="activeTab === 'edit'" class="modal-body">
          <!-- Archived warning -->
          <div v-if="prompt.status === 'archived'" class="info-banner info-orange">
            {{ $t('bo.aiPrompts.archivedNotEditable') }}
          </div>

          <!-- Published info -->
          <div v-if="prompt.status === 'published'" class="info-banner info-blue">
            {{ $t('bo.aiPrompts.publishedWillCreateVersion') }}
          </div>

          <div v-if="saveError" class="error-banner">{{ saveError }}</div>

          <!-- Name -->
          <div class="form-group">
            <label>{{ $t('bo.aiPrompts.labelName') }} <span class="required">*</span></label>
            <input v-model="form.name" type="text" class="input" :disabled="isArchived" />
          </div>

          <div class="form-row">
            <!-- Assistant -->
            <div class="form-group">
              <label>{{ $t('bo.aiPrompts.labelAssistant') }}</label>
              <select v-model="form.assistant" class="input" :disabled="isArchived">
                <option value="sparkie_bot">Sparkie Bot</option>
              </select>
            </div>

            <!-- Use case -->
            <div class="form-group">
              <label>{{ $t('bo.aiPrompts.labelUseCase') }}</label>
              <select v-model="form.use_case" class="input" :disabled="isArchived">
                <option value="goal_suggestion">{{ $t('bo.aiPrompts.useCaseGoalSuggestion') }}</option>
                <option value="bio_generation">{{ $t('bo.aiPrompts.useCaseBioGeneration') }}</option>
                <option value="matching">{{ $t('bo.aiPrompts.useCaseMatching') }}</option>
                <option value="session_preparation">{{ $t('bo.aiPrompts.useCaseSessionPrep') }}</option>
              </select>
            </div>
          </div>

          <div class="form-row">
            <!-- Provider -->
            <div class="form-group">
              <label>{{ $t('bo.aiPrompts.labelProvider') }}</label>
              <select v-model="form.ai_provider" class="input" :disabled="isArchived">
                <option value="mistral">Mistral</option>
                <option value="openai">OpenAI</option>
              </select>
            </div>

            <!-- Model -->
            <div class="form-group">
              <label>{{ $t('bo.aiPrompts.labelModel') }}</label>
              <input v-model="form.model" type="text" class="input" :disabled="isArchived" />
            </div>
          </div>

          <div class="form-row">
            <!-- Language (hidden for bio_generation: always auto / same as input) -->
            <div v-if="form.use_case !== 'bio_generation'" class="form-group">
              <label>{{ $t('bo.aiPrompts.labelLanguage') }}</label>
              <select v-model="form.output_language" class="input" :disabled="isArchived">
                <option value="auto">{{ $t('bo.aiPrompts.langAuto') }}</option>
                <option value="fr">Français (fr)</option>
                <option value="en">English (en)</option>
              </select>
            </div>
            <div v-else class="form-group">
              <label>{{ $t('bo.aiPrompts.labelLanguage') }}</label>
              <div class="input input-readonly">{{ $t('bo.aiPrompts.langAuto') }}</div>
              <div class="hint-line">{{ $t('bo.aiPrompts.langAutoBioHint') }}</div>
            </div>

            <!-- Output format -->
            <div class="form-group">
              <label>{{ $t('bo.aiPrompts.labelOutputFormat') }}</label>
              <select v-model="form.expected_output_format" class="input" :disabled="isArchived">
                <option value="plain_text">Plain text</option>
                <option value="json">JSON</option>
                <option value="bullet_list">Bullet list</option>
                <option value="numbered_list">Numbered list</option>
                <option value="markdown">Markdown</option>
              </select>
            </div>
          </div>

          <div class="form-row">
            <!-- Temperature -->
            <div class="form-group">
              <label>{{ $t('bo.aiPrompts.labelTemperature') }}
                <span class="hint">{{ form.temperature }}</span>
              </label>
              <input v-model.number="form.temperature" type="range" min="0" max="1" step="0.05" class="range-input" :disabled="isArchived" />
            </div>

            <!-- Max tokens -->
            <div class="form-group">
              <label>{{ $t('bo.aiPrompts.labelMaxTokens') }}</label>
              <input v-model.number="form.max_tokens" type="number" class="input input-sm" min="100" max="8000" step="100" :disabled="isArchived" />
            </div>
          </div>

          <!-- Execution mode -->
          <div class="form-group">
            <label>{{ $t('bo.aiPrompts.labelExecutionMode') }}</label>
            <select v-model="form.execution_mode" class="input input-sm" :disabled="isArchived">
              <option value="single">Single</option>
              <option value="chain_step">Chain step</option>
            </select>
          </div>

          <!-- System prompt -->
          <div class="form-group">
            <label>{{ $t('bo.aiPrompts.labelSystemPrompt') }} <span class="required">*</span></label>
            <div class="hint-line">{{ $t('bo.aiPrompts.variableHint') }}</div>
            <textarea v-model="form.system_prompt" class="textarea textarea-tall" rows="6" :disabled="isArchived" />
          </div>

          <!-- User prompt template -->
          <div class="form-group">
            <label>{{ $t('bo.aiPrompts.labelUserPrompt') }} <span class="required">*</span></label>
            <textarea v-model="form.user_prompt_template" class="textarea textarea-tall" rows="6" :disabled="isArchived" />
          </div>

          <!-- Variables -->
          <div class="form-group">
            <label>{{ $t('bo.aiPrompts.labelVariables') }}</label>
            <div class="tag-input-area">
              <div class="tag-list">
                <span v-for="(v, i) in form.variables" :key="i" class="tag-item">
                  {{ '{' + '{' + v + '}' + '}' }}
                  <button v-if="!isArchived" @click="removeVariable(i)" class="tag-remove">&times;</button>
                </span>
              </div>
              <input
                v-if="!isArchived"
                v-model="variableInput"
                @keydown.enter.prevent="addVariable"
                type="text"
                class="input"
                :placeholder="$t('bo.aiPrompts.variablePlaceholder')"
              />
            </div>
          </div>

          <!-- Garde-fou placeholders / variables (non bloquant) -->
          <div
            v-if="undeclaredPlaceholders.length || unusedVariables.length || isBioUseCase"
            class="prompt-lint"
          >
            <div v-if="undeclaredPlaceholders.length" class="lint-warn">
              ⚠ {{ $t('bo.aiPrompts.warnUndeclaredPlaceholders', { list: undeclaredPlaceholders.join(', ') }) }}
            </div>
            <div v-if="unusedVariables.length" class="lint-info">
              {{ $t('bo.aiPrompts.warnUnusedVariables', { list: unusedVariables.join(', ') }) }}
            </div>
            <div v-if="isBioUseCase" class="lint-info">
              {{ $t('bo.aiPrompts.hintBioLanguage') }}
            </div>
          </div>

          <!-- Internal notes -->
          <div class="form-group">
            <label>{{ $t('bo.aiPrompts.labelNotes') }}</label>
            <textarea v-model="form.internal_notes" class="textarea" rows="2" :disabled="isArchived" />
          </div>
        </div>

        <!-- Tab: Versions -->
        <div v-if="activeTab === 'versions'" class="modal-body">
          <div v-if="versionsLoading" class="loading">{{ $t('bo.common.loading') }}</div>
          <div v-else-if="versions.length === 0" class="empty">{{ $t('bo.aiPrompts.noVersions') }}</div>
          <table v-else class="versions-table">
            <thead>
              <tr>
                <th>{{ $t('bo.aiPrompts.versionCol') }}</th>
                <th>{{ $t('bo.aiPrompts.versionStatus') }}</th>
                <th>{{ $t('bo.aiPrompts.versionDate') }}</th>
                <th>{{ $t('bo.aiPrompts.versionModel') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="v in versions" :key="v.id" :class="{ 'row-current': v.id === prompt.id }">
                <td>
                  v{{ v.version }}
                  <span v-if="v.id === prompt.id" class="current-chip">{{ $t('bo.aiPrompts.currentVersion') }}</span>
                </td>
                <td>
                  <span :class="['badge', `badge-${v.status}`]">{{ v.status }}</span>
                </td>
                <td>{{ formatDate(v.published_at || v.created_at) }}</td>
                <td>{{ v.model }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <!-- Footer -->
      <div class="modal-footer" v-if="!loading && !loadError && prompt && activeTab === 'edit'">
        <button @click="$emit('close')" class="btn-cancel">{{ $t('bo.common.cancel') }}</button>
        <button
          v-if="!isArchived"
          @click="save"
          :disabled="saving || !isValid"
          class="btn-submit"
        >
          {{ saving ? $t('bo.common.saving') : $t('bo.common.save') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import aiPromptService from '@/services/aiPromptService'

const { t } = useI18n()

const props = defineProps({
  promptId: { type: [Number, String], required: true },
  showVersions: { type: Boolean, default: false },
})

const emit = defineEmits(['close', 'updated'])

const prompt = ref(null)
const loading = ref(true)
const loadError = ref('')
const saving = ref(false)
const saveError = ref('')
const activeTab = ref(props.showVersions ? 'versions' : 'edit')
const versions = ref([])
const versionsLoading = ref(false)
const variableInput = ref('')

const form = ref({
  name: '',
  assistant: 'sparkie_bot',
  use_case: 'goal_suggestion',
  execution_mode: 'single',
  ai_provider: 'mistral',
  model: '',
  output_language: 'fr',
  temperature: 0.7,
  max_tokens: 800,
  system_prompt: '',
  user_prompt_template: '',
  variables: [],
  expected_output_format: 'plain_text',
  internal_notes: '',
})

const isArchived = computed(() => prompt.value?.status === 'archived')

const isValid = computed(() =>
  form.value.name.trim() &&
  form.value.system_prompt.trim() &&
  form.value.user_prompt_template.trim() &&
  form.value.model.trim()
)

// --- Garde-fou : cohérence placeholders <-> variables (non bloquant) ---
const extractPlaceholders = (text) => {
  const found = new Set()
  const re = /\{\{\s*([\w.]+)\s*\}\}/g
  let m
  while ((m = re.exec(text || '')) !== null) found.add(m[1])
  return [...found]
}
const usedPlaceholders = computed(() => [
  ...new Set([
    ...extractPlaceholders(form.value.system_prompt),
    ...extractPlaceholders(form.value.user_prompt_template),
  ]),
])
// Placeholders écrits dans les prompts mais absents de "Variables" => jamais interpolés.
const undeclaredPlaceholders = computed(() =>
  usedPlaceholders.value.filter((p) => !form.value.variables.includes(p))
)
// Variables déclarées mais jamais utilisées dans les prompts.
const unusedVariables = computed(() =>
  form.value.variables.filter((v) => !usedPlaceholders.value.includes(v))
)
const isBioUseCase = computed(() => form.value.use_case === 'bio_generation')

const capitalize = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : ''

const modalTitle = computed(() => {
  if (!prompt.value) return t('bo.aiPrompts.editTitle')
  return `${t('bo.aiPrompts.editTitle')} — ${prompt.value.name}`
})

const fillForm = (data) => {
  const useCase = data.use_case || 'goal_suggestion'
  form.value = {
    name: data.name || '',
    assistant: data.assistant || 'sparkie_bot',
    use_case: useCase,
    execution_mode: data.execution_mode || 'single',
    ai_provider: data.ai_provider || 'mistral',
    model: data.model || '',
    output_language: useCase === 'bio_generation' ? 'auto' : (data.output_language || 'fr'),
    temperature: parseFloat(data.temperature) || 0.7,
    max_tokens: data.max_tokens || 800,
    system_prompt: data.system_prompt || '',
    user_prompt_template: data.user_prompt_template || '',
    variables: Array.isArray(data.variables) ? [...data.variables] : [],
    expected_output_format: data.expected_output_format || 'plain_text',
    internal_notes: data.internal_notes || '',
  }
}

const loadPrompt = async () => {
  loading.value = true
  loadError.value = ''
  try {
    const res = await aiPromptService.getOne(props.promptId)
    prompt.value = res.data?.data || res.data
    fillForm(prompt.value)
    if (props.showVersions) loadVersionsTab()
  } catch {
    loadError.value = t('bo.aiPrompts.failedLoad')
  } finally {
    loading.value = false
  }
}

const loadVersionsTab = async () => {
  activeTab.value = 'versions'
  if (versions.value.length) return
  versionsLoading.value = true
  try {
    const res = await aiPromptService.getVersions(props.promptId)
    versions.value = res.data?.data || []
  } catch {
    versions.value = []
  } finally {
    versionsLoading.value = false
  }
}

const save = async () => {
  if (!isValid.value || isArchived.value) return
  saving.value = true
  saveError.value = ''
  try {
    const payload = { ...form.value }
    if (payload.use_case === 'bio_generation') {
      payload.output_language = 'auto'
    }
    if (!payload.internal_notes) delete payload.internal_notes
    const res = await aiPromptService.update(props.promptId, payload)
    const updated = res.data
    if (updated?.new_version) {
      alert(t('bo.aiPrompts.newVersionCreated'))
    }
    emit('updated')
  } catch (e) {
    saveError.value = e.response?.data?.message || t('bo.aiPrompts.failedSave')
    if (e.response?.data?.errors) {
      const errs = Object.values(e.response.data.errors).flat()
      saveError.value = errs.join(' ')
    }
  } finally {
    saving.value = false
  }
}

const addVariable = () => {
  const val = variableInput.value.trim().replace(/[{}]/g, '')
  if (val && !form.value.variables.includes(val)) {
    form.value.variables.push(val)
    variableInput.value = ''
  }
}

const removeVariable = (i) => form.value.variables.splice(i, 1)

const formatDate = (dateStr) => {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
}

watch(
  () => form.value.use_case,
  (useCase) => {
    if (useCase === 'bio_generation') {
      form.value.output_language = 'auto'
    }
  }
)

onMounted(loadPrompt)
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal {
  background: white;
  border-radius: 0.75rem;
  width: 100%;
  max-width: 760px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  gap: 1rem;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  overflow: hidden;
}

.modal-header h2 {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  line-height: 1;
  padding: 0 0.25rem;
  flex-shrink: 0;
}

.version-chip {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  background: #f3f4f6;
  color: #6b7280;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.tabs {
  display: flex;
  gap: 0;
  border-bottom: 2px solid #e5e7eb;
  flex-shrink: 0;
}

.tab {
  padding: 0.75rem 1.5rem;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
  transition: color 0.15s;
}

.tab:hover { color: #374151; }

.tab.active {
  color: #3b82f6;
  border-bottom-color: #3b82f6;
  font-weight: 600;
}

.loading, .empty {
  padding: 3rem;
  text-align: center;
  color: #6b7280;
}

.error-full {
  padding: 2rem;
  text-align: center;
  color: #dc2626;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.error-banner {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.info-banner {
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  border: 1px solid;
}

.info-blue { background: #eff6ff; border-color: #bfdbfe; color: #1d4ed8; }
.info-orange { background: #fffbeb; border-color: #fde68a; color: #92400e; }

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.form-group label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.required { color: #dc2626; }

.hint {
  font-weight: 400;
  color: #6b7280;
  font-size: 0.8rem;
}

.hint-line {
  font-size: 0.8rem;
  color: #6b7280;
  font-style: italic;
}

.prompt-lint {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 1rem;
}
.prompt-lint .lint-warn {
  font-size: 0.8rem;
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 0.375rem;
  padding: 0.4rem 0.6rem;
}
.prompt-lint .lint-info {
  font-size: 0.8rem;
  color: #6b7280;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  padding: 0.4rem 0.6rem;
}

.input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #1f2937;
  width: 100%;
  box-sizing: border-box;
}

.input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
}

.input:disabled { background: #f9fafb; color: #6b7280; cursor: not-allowed; }

.input-readonly {
  background: #f9fafb;
  color: #374151;
  display: flex;
  align-items: center;
  min-height: 2.25rem;
}

.input-sm { max-width: 160px; }

.range-input {
  width: 100%;
  cursor: pointer;
  accent-color: #3b82f6;
}

.range-input:disabled { cursor: not-allowed; opacity: 0.5; }

.textarea {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #1f2937;
  resize: vertical;
  font-family: inherit;
}

.textarea:focus {
  outline: none;
  border-color: #3b82f6;
}

.textarea:disabled { background: #f9fafb; color: #6b7280; cursor: not-allowed; }

.textarea-tall {
  font-family: 'Courier New', monospace;
  font-size: 0.8rem;
  line-height: 1.6;
}

.tag-input-area {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  min-height: 1rem;
}

.tag-item {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.6rem;
  background: #f0fdf4;
  color: #15803d;
  border-radius: 9999px;
  font-size: 0.8rem;
  font-family: monospace;
}

.tag-remove {
  background: none;
  border: none;
  cursor: pointer;
  color: #15803d;
  font-size: 1rem;
  line-height: 1;
  padding: 0;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.btn-cancel {
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: #374151;
}

.btn-cancel:hover { background: #f9fafb; }

.btn-submit {
  padding: 0.5rem 1.25rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
}

.btn-submit:hover:not(:disabled) { background: #2563eb; }
.btn-submit:disabled { opacity: 0.5; cursor: not-allowed; }

/* Versions table */
.versions-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.versions-table th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.versions-table td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f3f4f6;
}

.versions-table tr:last-child td {
  border-bottom: none;
}

.row-current {
  background: #eff6ff;
}

.current-chip {
  display: inline-block;
  margin-left: 0.5rem;
  padding: 0.1rem 0.4rem;
  background: #dbeafe;
  color: #1d4ed8;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 600;
}

.badge {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.badge-draft { background: #fef3c7; color: #92400e; }
.badge-published { background: #d1fae5; color: #065f46; }
.badge-archived { background: #f3f4f6; color: #6b7280; }
</style>
